import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { TelemetryService } from './telemetry.service';

/**
 * Global error handler that catches all unhandled errors.
 * Logs errors and displays user-friendly notifications.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly telemetry = inject(TelemetryService);

  handleError(error: unknown): void {
    // PrimeNG v20.x InputText calls detectChanges() in ngAfterViewInit, which
    // triggers NG0103 (infinite change detection) with Angular's signal-based CD.
    // The initial error cascades into more NG0103 errors from Angular's CD loop.
    // This is harmless — the UI renders correctly.
    // TODO: Remove after upgrading to PrimeNG v21+
    if (this.isPrimeNGChangeDetectionBug(error)) {
      return;
    }

    // Ensure we're running in Angular zone for change detection
    this.zone.run(() => {
      // Log ALWAYS, even in production (use structured logging service in future)
      console.error('[GlobalErrorHandler] Unhandled error:', error);

      // Ship it. Without this a production error exists only in a console nobody
      // is watching, and support has no record that anything happened.
      this.telemetry.report(error, 'GlobalErrorHandler');

      // Extract message
      const message = this.extractMessage(error);

      // Check for specific error types
      if (this.isChunkLoadError(error)) {
        // A lazy chunk could not be loaded — the build it was emitted against is
        // gone (dev-server rebuild / dep re-optimization, or a production deploy
        // that changed chunk hashes). Reloading pulls a fresh index with current
        // chunk references. Guard against reload loops if the chunk is truly gone.
        this.reloadForChunkError();
        return;
      }

      if (this.isAuthError(error)) {
        this.router.navigate(['/login']);
        return;
      }

      // TODO: Integrate with toast/notification service
      // this.notificationService.error(message);

      // For now, alert as placeholder (remove in production)
      // alert(`An error occurred: ${message}`);
    });
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  }

  private isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) {
      return false;
    }
    const message = error.message;
    return (
      // Webpack builder
      message.includes('ChunkLoadError') ||
      message.includes('Loading chunk') ||
      // esbuild/Vite builder (Chromium): "Failed to fetch dynamically imported module"
      message.includes('Failed to fetch dynamically imported module') ||
      // Firefox variant
      message.includes('error loading dynamically imported module') ||
      // Safari variant
      message.includes('Importing a module script failed')
    );
  }

  /**
   * Reload once to recover from a stale chunk reference, guarding against an
   * infinite reload loop when the chunk is genuinely unreachable (e.g. a cached
   * index.html pointing at a deleted chunk). Falls through to logging if we
   * already attempted a reload moments ago.
   */
  private reloadForChunkError(): void {
    const KEY = 'chunk-reload-attempt';
    const RETRY_WINDOW_MS = 10_000;
    const previousAttempt = Number(sessionStorage.getItem(KEY) ?? '0');
    const now = Date.now();

    if (previousAttempt && now - previousAttempt < RETRY_WINDOW_MS) {
      // Already reloaded recently and still failing — stop to avoid a loop.
      return;
    }

    sessionStorage.setItem(KEY, String(now));

    // Send before the page goes away — a spike here means a deploy left clients
    // pointing at chunk hashes that no longer exist.
    this.telemetry.flush();
    this.reloadPage();
  }

  /** Wrapper around the real page reload so it can be stubbed in tests. */
  private reloadPage(): void {
    window.location.reload();
  }

  private isAuthError(error: unknown): boolean {
    if (error && typeof error === 'object' && 'status' in error) {
      return (error as { status: number }).status === 401;
    }
    return false;
  }

  /**
   * Detects NG0103 (infinite change detection) errors caused by PrimeNG v20.x
   * InputText calling detectChanges() in ngAfterViewInit.
   * The initial error from InputText triggers cascading NG0103 errors from
   * Angular's CD loop that don't have PrimeNG in their stack trace.
   * TODO: Remove after upgrading to PrimeNG v21+
   */
  private isPrimeNGChangeDetectionBug(error: unknown): boolean {
    return error instanceof Error && error.message.includes('NG0103');
  }
}
